<script setup lang="ts">
import {
  computed,
  inject,
  provide,
  ref,
  watch,
  type Ref,
} from 'vue'
import type {
  AnimationEasing,
  AnimationInterpolation,
  SemanticCameraMotionLeafSemantic,
  SemanticCameraMotionLeafTemplate,
  SemanticCameraMotionSequenceStep,
  SemanticCameraMotionSequenceTemplate,
  SemanticCameraMotionTemplate,
} from '@sharefe/share3d-engine'

interface SemanticOption {
  key: SemanticCameraMotionTemplate['semantic']
  label: string
  detail: string
}

interface ParamField {
  key: string
  label: string
  type: 'number' | 'select'
  min?: number
  max?: number
  step?: number
  rangeByDirection?: Record<string, { min: number; max: number }>
  options?: Array<{ value: string; label: string }>
}

interface SelectedStepLocation {
  step: SemanticCameraMotionSequenceStep
  parent: SemanticCameraMotionSequenceTemplate
  index: number
  depth: number
}

interface MotionEditorSelectionContext {
  selectedStep: Ref<SemanticCameraMotionSequenceStep | null>
  collapsedSteps: Ref<Set<SemanticCameraMotionSequenceStep>>
  stepKeys: WeakMap<SemanticCameraMotionSequenceStep, string>
  nextStepKey: number
}

const MOTION_EDITOR_SELECTION_KEY = 'camera-motion-editor-selection'

const props = withDefaults(
  defineProps<{
    leafTemplates: SemanticCameraMotionLeafTemplate[]
    semanticOptions: SemanticOption[]
    paramFields: Record<string, ParamField[]>
    depth?: number
    root?: boolean
    ownerStep?: SemanticCameraMotionSequenceStep
  }>(),
  { depth: 0, root: false }
)
const model = defineModel<SemanticCameraMotionTemplate>({ required: true })

const inheritedSelection = inject<MotionEditorSelectionContext | null>(
  MOTION_EDITOR_SELECTION_KEY,
  null
)
const editorSelection: MotionEditorSelectionContext = inheritedSelection ?? {
  selectedStep: ref<SemanticCameraMotionSequenceStep | null>(null),
  collapsedSteps: ref(new Set<SemanticCameraMotionSequenceStep>()),
  stepKeys: new WeakMap<SemanticCameraMotionSequenceStep, string>(),
  nextStepKey: 1,
}
if (!inheritedSelection) {
  provide(MOTION_EDITOR_SELECTION_KEY, editorSelection)
}

const sequenceTemplate = computed(() =>
  model.value.semantic === 'sequence' ? model.value : null
)
const canNest = computed(() => props.depth < 7)
const selectedStepLocation = computed(() => {
  const sequence = props.root && model.value.semantic === 'sequence' ? model.value : null
  const selected = editorSelection.selectedStep.value
  return sequence && selected ? findStepLocation(sequence, selected) : null
})
const selectedStep = computed(() => selectedStepLocation.value?.step ?? null)
const selectedTemplate = computed(() => selectedStep.value?.template ?? null)
const selectedLeafTemplate = computed(() => {
  const template = selectedTemplate.value
  return template && template.semantic !== 'sequence' ? template : null
})
const selectedSequenceTemplate = computed(() => {
  const template = selectedTemplate.value
  return template?.semantic === 'sequence' ? template : null
})
const selectedLeafFields = computed(() => {
  const template = selectedLeafTemplate.value
  return template ? (props.paramFields[template.semantic] ?? []) : []
})
const selectedLeafParams = computed<Record<string, string | number>>(() =>
  (selectedLeafTemplate.value?.params ?? {}) as Record<string, string | number>
)
const canSelectedStepUseSequence = computed(() => {
  const location = selectedStepLocation.value
  return !location || location.step.template.semantic === 'sequence' || location.depth < 7
})

let nextSequenceId = 1

watch(
  () => model.value,
  (template) => {
    if (!props.root) {
      return
    }
    editorSelection.collapsedSteps.value = new Set()
    editorSelection.selectedStep.value =
      template.semantic === 'sequence' ? (template.params.steps[0] ?? null) : null
  },
  { immediate: true }
)

function cloneTemplate<T extends SemanticCameraMotionTemplate>(template: T): T {
  return JSON.parse(JSON.stringify(template)) as T
}

function getFallbackLeaf(): SemanticCameraMotionLeafTemplate {
  const template = props.leafTemplates[0]
  if (!template) {
    throw new Error('组合运镜编辑器缺少可用的叶子运镜模板')
  }
  return cloneTemplate(template)
}

function createSequenceTemplate(): SemanticCameraMotionSequenceTemplate {
  return {
    kind: 'share3d.camera-motion-semantic',
    version: 1,
    id: `camera-sequence-child-${nextSequenceId++}`,
    name: '子组合运镜',
    description: '按顺序播放的子组合运镜',
    semantic: 'sequence',
    params: { steps: [] },
    holdDuration: 0,
  }
}

function getSemanticLabel(semantic: SemanticCameraMotionTemplate['semantic']): string {
  return props.semanticOptions.find((option) => option.key === semantic)?.label ?? semantic
}

function getLeafBySemantic(
  semantic: SemanticCameraMotionLeafSemantic
): SemanticCameraMotionLeafTemplate {
  return cloneTemplate(
    props.leafTemplates.find((template) => template.semantic === semantic) ?? getFallbackLeaf()
  )
}

function findStepLocation(
  sequence: SemanticCameraMotionSequenceTemplate,
  selected: SemanticCameraMotionSequenceStep,
  depth = 0
): SelectedStepLocation | null {
  for (let index = 0; index < sequence.params.steps.length; index++) {
    const step = sequence.params.steps[index]
    if (step === selected) {
      return { step, parent: sequence, index, depth }
    }
    if (step.template.semantic === 'sequence') {
      const nested = findStepLocation(step.template, selected, depth + 1)
      if (nested) {
        return nested
      }
    }
  }
  return null
}

function templateContainsStep(
  template: SemanticCameraMotionTemplate,
  selected: SemanticCameraMotionSequenceStep
): boolean {
  if (template.semantic !== 'sequence') {
    return false
  }
  return template.params.steps.some(
    (step) => step === selected || templateContainsStep(step.template, selected)
  )
}

function getStepKey(step: SemanticCameraMotionSequenceStep): string {
  const existing = editorSelection.stepKeys.get(step)
  if (existing) {
    return existing
  }
  const key = `camera-motion-step-${editorSelection.nextStepKey++}`
  editorSelection.stepKeys.set(step, key)
  return key
}

function selectStep(step: SemanticCameraMotionSequenceStep): void {
  editorSelection.selectedStep.value = step
}

function isSelectedStep(step: SemanticCameraMotionSequenceStep): boolean {
  return editorSelection.selectedStep.value === step
}

function isStepCollapsed(step: SemanticCameraMotionSequenceStep): boolean {
  return editorSelection.collapsedSteps.value.has(step)
}

function toggleStepCollapsed(step: SemanticCameraMotionSequenceStep): void {
  const collapsed = new Set(editorSelection.collapsedSteps.value)
  if (collapsed.has(step)) {
    collapsed.delete(step)
  } else {
    const selected = editorSelection.selectedStep.value
    if (selected && selected !== step && templateContainsStep(step.template, selected)) {
      editorSelection.selectedStep.value = step
    }
    collapsed.add(step)
  }
  editorSelection.collapsedSteps.value = collapsed
}

function removeCollapsedState(
  step: SemanticCameraMotionSequenceStep,
  collapsed: Set<SemanticCameraMotionSequenceStep>
): void {
  collapsed.delete(step)
  if (step.template.semantic === 'sequence') {
    for (const child of step.template.params.steps) {
      removeCollapsedState(child, collapsed)
    }
  }
}

function replaceSelectedStepTemplate(event: Event): void {
  const location = selectedStepLocation.value
  if (!location) {
    return
  }
  const semantic = (event.target as HTMLSelectElement).value as
    SemanticCameraMotionTemplate['semantic']
  if (semantic === 'sequence' && !canSelectedStepUseSequence.value) {
    return
  }
  location.step.template =
    semantic === 'sequence' ? createSequenceTemplate() : getLeafBySemantic(semantic)
  const collapsed = new Set(editorSelection.collapsedSteps.value)
  collapsed.delete(location.step)
  editorSelection.collapsedSteps.value = collapsed
}

function addLeafStep(): void {
  const sequence = sequenceTemplate.value
  if (!sequence || sequence.params.steps.length >= 32) {
    return
  }
  const step: SemanticCameraMotionSequenceStep = {
    durationWeight: 1,
    template: getFallbackLeaf(),
  }
  sequence.params.steps.push(step)
  selectStep(sequence.params.steps[sequence.params.steps.length - 1])
}

function addSequenceStep(): void {
  const sequence = sequenceTemplate.value
  if (!sequence || !canNest.value || sequence.params.steps.length >= 32) {
    return
  }
  const step: SemanticCameraMotionSequenceStep = {
    durationWeight: 1,
    template: createSequenceTemplate(),
  }
  sequence.params.steps.push(step)
  selectStep(sequence.params.steps[sequence.params.steps.length - 1])
}

function removeStep(index: number): void {
  const sequence = sequenceTemplate.value
  if (!sequence || index < 0 || index >= sequence.params.steps.length) {
    return
  }
  const removed = sequence.params.steps[index]
  const selected = editorSelection.selectedStep.value
  const removesSelection =
    selected !== null &&
    (removed === selected || templateContainsStep(removed.template, selected))
  sequence.params.steps.splice(index, 1)
  normalizeFirstStep(sequence.params.steps)

  const collapsed = new Set(editorSelection.collapsedSteps.value)
  removeCollapsedState(removed, collapsed)
  editorSelection.collapsedSteps.value = collapsed

  if (removesSelection) {
    editorSelection.selectedStep.value =
      sequence.params.steps[index] ??
      sequence.params.steps[index - 1] ??
      props.ownerStep ??
      null
  }
}

function moveStep(index: number, direction: -1 | 1): void {
  const steps = sequenceTemplate.value?.params.steps
  const nextIndex = index + direction
  if (!steps || nextIndex < 0 || nextIndex >= steps.length) {
    return
  }
  const [step] = steps.splice(index, 1)
  steps.splice(nextIndex, 0, step)
  normalizeFirstStep(steps)
}

function normalizeFirstStep(steps: SemanticCameraMotionSequenceStep[]): void {
  if (steps[0]) {
    delete steps[0].startOffset
  }
}

function normalizeWeight(step: SemanticCameraMotionSequenceStep): void {
  const value = Number(step.durationWeight)
  step.durationWeight = Number.isFinite(value) && value > 0 ? value : 1
}

function getOffsetValue(
  step: SemanticCameraMotionSequenceStep,
  target: 'camera' | 'target',
  field: 'yaw' | 'pitch' | 'distanceScale'
): number {
  return step.startOffset?.[target]?.[field] ?? (field === 'distanceScale' ? 1 : 0)
}

function setOffsetValue(
  step: SemanticCameraMotionSequenceStep,
  target: 'camera' | 'target',
  field: 'yaw' | 'pitch' | 'distanceScale',
  event: Event
): void {
  const fallback = field === 'distanceScale' ? 1 : 0
  const rawValue = Number((event.target as HTMLInputElement).value)
  const min = field === 'distanceScale' ? 0.25 : -180
  const max = field === 'distanceScale' ? 4 : 180
  const value = Number.isFinite(rawValue) ? Math.min(max, Math.max(min, rawValue)) : fallback
  step.startOffset ??= {}
  step.startOffset[target] ??= {}
  step.startOffset[target]![field] = value
}

function updateLeafParam(field: ParamField, event: Event): void {
  const leaf = selectedLeafTemplate.value
  if (!leaf) {
    return
  }
  const params = { ...((leaf.params ?? {}) as Record<string, string | number>) }
  const input = event.target as HTMLInputElement | HTMLSelectElement
  if (field.type === 'select') {
    params[field.key] = input.value
  } else {
    const rawValue = Number(input.value)
    const { min, max } = getLeafParamRange(field, params)
    params[field.key] = Number.isFinite(rawValue) ? Math.min(max, Math.max(min, rawValue)) : min
  }
  normalizeDependentLeafParams(leaf.semantic, params, field.key)
  leaf.params = params
}

function getLeafParamRange(
  field: ParamField,
  params: Record<string, string | number> = selectedLeafParams.value,
): { min: number; max: number } {
  const direction = String(params.direction ?? '')
  const directionalRange = field.rangeByDirection?.[direction]
  return {
    min: directionalRange?.min ?? field.min ?? Number.NEGATIVE_INFINITY,
    max: directionalRange?.max ?? field.max ?? Number.POSITIVE_INFINITY,
  }
}

function normalizeDependentLeafParams(
  semantic: SemanticCameraMotionLeafSemantic,
  params: Record<string, string | number>,
  changedKey: string
): void {
  if (semantic === 'dolly') {
    const direction = String(params.direction ?? 'in')
    const distanceScale = Number(params.distanceScale)
    if (changedKey === 'direction' || !Number.isFinite(distanceScale)) {
      params.distanceScale = direction === 'out' ? 1.5 : 0.35
    } else if (direction === 'in') {
      params.distanceScale = Math.min(0.99, Math.max(0, distanceScale))
    } else if (direction === 'out' && distanceScale <= 1) {
      params.distanceScale = 1.01
    }
  }

  if (semantic === 'spiral') {
    const radiusMode = String(params.radiusMode ?? 'fixed')
    const radiusScale = Number(params.radiusScale)
    if (radiusMode === 'fixed') {
      params.radiusScale = 1
    } else if (changedKey === 'radiusMode' || !Number.isFinite(radiusScale)) {
      params.radiusScale = radiusMode === 'in' ? 0.65 : 1.35
    } else if (radiusMode === 'in' && radiusScale >= 1) {
      params.radiusScale = 0.99
    } else if (radiusMode === 'out' && radiusScale <= 1) {
      params.radiusScale = 1.01
    }
  }
}

function normalizeHoldDuration(template: SemanticCameraMotionTemplate): void {
  const value = Number(template.holdDuration)
  template.holdDuration = Number.isFinite(value) ? Math.min(30, Math.max(0, value)) : 0
}

function updateLeafInterpolation(event: Event): void {
  const leaf = selectedLeafTemplate.value
  if (!leaf) {
    return
  }
  leaf.interpolation = (event.target as HTMLSelectElement).value as AnimationInterpolation
  if (leaf.interpolation === 'step') {
    delete leaf.easing
    delete leaf.segmentEasing
  }
}

function updateLeafEasing(event: Event): void {
  const leaf = selectedLeafTemplate.value
  if (!leaf || leaf.interpolation === 'step') {
    return
  }
  leaf.easing = (event.target as HTMLSelectElement).value as AnimationEasing
}
</script>

<template>
  <div class="motion-node" :class="{ 'motion-node-root': root }">
    <template v-if="sequenceTemplate">
      <section v-if="root && selectedStepLocation && selectedTemplate" class="motion-selected-editor">
        <div class="motion-selected-header">
          <div>
            <span class="motion-section-label">当前选中运镜</span>
            <strong>
              步骤 {{ selectedStepLocation.index + 1 }} ·
              {{ selectedTemplate.name }}
            </strong>
          </div>
          <span class="motion-selected-depth">层级 {{ selectedStepLocation.depth + 1 }}</span>
        </div>

        <div class="motion-node-meta">
          <label>
            <span>节点名称</span>
            <input v-model.trim="selectedTemplate.name" type="text" maxlength="64" />
          </label>
          <label>
            <span>节点定格</span>
            <input
              v-model.number="selectedTemplate.holdDuration"
              type="number"
              min="0"
              max="30"
              step="0.1"
              @change="normalizeHoldDuration(selectedTemplate)"
            />
          </label>
          <label>
            <span>子模板类型</span>
            <select :value="selectedTemplate.semantic" @change="replaceSelectedStepTemplate">
              <option
                v-for="option in semanticOptions"
                :key="option.key"
                :value="option.key"
                :disabled="option.key === 'sequence' && !canSelectedStepUseSequence"
              >{{ option.label }}</option>
            </select>
          </label>
          <label>
            <span>时长权重</span>
            <input
              v-model.number="selectedStepLocation.step.durationWeight"
              type="number"
              min="0.01"
              max="100"
              step="0.1"
              @change="normalizeWeight(selectedStepLocation.step)"
            />
          </label>
        </div>

        <p v-if="selectedStepLocation.index === 0" class="motion-first-step-hint">
          当前步骤是所在组合的第 1 个运镜，直接使用该组合的初始相机和目标点，不进行起点重定位。
        </p>
        <div v-else class="motion-offset-section">
          <div class="motion-offset-title">
            <strong>步骤 {{ selectedStepLocation.index + 1 }} · 起点重定位</strong>
            <span>根据上一步结束的相机 B / 目标 B，分别计算本步骤的新相机与新目标点。</span>
          </div>
          <div class="motion-offsets">
            <div
              v-for="target in (['camera', 'target'] as const)"
              :key="target"
              class="motion-offset-group"
            >
              <strong>
                {{ target === 'camera' ? '新相机位置（以目标 B 为球心）' : '新目标点位置（以新相机为球心）' }}
              </strong>
              <label
                v-for="field in (['yaw', 'pitch', 'distanceScale'] as const)"
                :key="field"
              >
                <span>
                  {{ field === 'yaw' ? '东西方向偏离角' : field === 'pitch' ? '南北方向偏离角' : '距离倍率' }}
                </span>
                <input
                  :value="getOffsetValue(selectedStepLocation.step, target, field)"
                  type="number"
                  :min="field === 'distanceScale' ? 0.25 : -180"
                  :max="field === 'distanceScale' ? 4 : 180"
                  :step="field === 'distanceScale' ? 0.01 : 1"
                  @change="setOffsetValue(selectedStepLocation.step, target, field, $event)"
                />
              </label>
            </div>
          </div>
        </div>

        <div v-if="selectedLeafTemplate" class="motion-leaf-editor">
          <div class="motion-node-meta">
            <label>
              <span>插值方式</span>
              <select :value="selectedLeafTemplate.interpolation" @change="updateLeafInterpolation">
                <option value="step">Step</option>
                <option value="linear">Linear</option>
                <option value="spline">Spline</option>
              </select>
            </label>
            <label>
              <span>整体缓动</span>
              <select
                :value="selectedLeafTemplate.easing ?? 'linear'"
                :disabled="selectedLeafTemplate.interpolation === 'step'"
                @change="updateLeafEasing"
              >
                <option value="linear">Linear</option>
                <option value="sineInOut">Sine InOut</option>
                <option value="quadraticInOut">Quadratic InOut</option>
                <option value="cubicInOut">Cubic InOut</option>
              </select>
            </label>
          </div>
          <div class="motion-leaf-params">
            <label v-for="field in selectedLeafFields" :key="field.key">
              <span>{{ field.label }}</span>
              <select
                v-if="field.type === 'select'"
                :value="selectedLeafParams[field.key]"
                @change="updateLeafParam(field, $event)"
              >
                <option v-for="option in field.options" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
              <input
                v-else
                :value="selectedLeafParams[field.key]"
                type="number"
                :min="getLeafParamRange(field).min"
                :max="getLeafParamRange(field).max"
                :step="field.step"
                @change="updateLeafParam(field, $event)"
              />
            </label>
          </div>
        </div>
        <p v-else-if="selectedSequenceTemplate" class="motion-sequence-detail-hint">
          当前节点是组合运镜，只负责子步骤顺序、权重和定格，不包含片段插值参数。可在下方结构树中展开并编辑它的孩子。
        </p>
      </section>

      <p v-else-if="root" class="motion-selected-empty">
        当前没有选中的子运镜。请先在组合结构中添加或选择一个步骤。
      </p>

      <div v-if="root" class="motion-tree-header">
        <div>
          <span class="motion-section-label">组合结构</span>
          <strong>{{ sequenceTemplate.params.steps.length }} 个直接步骤</strong>
        </div>
        <span>点击任意层级的步骤，仅编辑当前选中运镜。</span>
      </div>

      <p v-if="sequenceTemplate.params.steps.length === 0" class="motion-empty-hint">
        当前组合为空。添加至少两个子运镜后，可从第 2 个步骤开始设置新相机位置和新目标点位置。
      </p>

      <div v-else class="motion-tree-list">
        <div
          v-for="(step, index) in sequenceTemplate.params.steps"
          :key="getStepKey(step)"
          class="motion-tree-branch"
        >
          <div class="motion-tree-row" :class="{ active: isSelectedStep(step) }">
            <button
              v-if="step.template.semantic === 'sequence'"
              class="motion-tree-toggle"
              type="button"
              :title="isStepCollapsed(step) ? '展开子组合' : '收起子组合'"
              :aria-label="isStepCollapsed(step) ? '展开子组合' : '收起子组合'"
              @click="toggleStepCollapsed(step)"
            >{{ isStepCollapsed(step) ? '＋' : '－' }}</button>
            <span v-else class="motion-tree-toggle-spacer"></span>
            <button class="motion-tree-summary" type="button" @click="selectStep(step)">
              <strong>步骤 {{ index + 1 }} · {{ step.template.name }}</strong>
              <span>
                {{ getSemanticLabel(step.template.semantic) }} · 权重 {{ step.durationWeight ?? 1 }}
                <template v-if="step.template.semantic === 'sequence'">
                  · {{ step.template.params.steps.length }} 个子步骤
                </template>
              </span>
            </button>
            <div class="motion-tree-actions">
              <button type="button" :disabled="index === 0" @click="moveStep(index, -1)">
                上移
              </button>
              <button
                type="button"
                :disabled="index === sequenceTemplate.params.steps.length - 1"
                @click="moveStep(index, 1)"
              >下移</button>
              <button type="button" @click="removeStep(index)">删除</button>
            </div>
          </div>

          <div
            v-if="step.template.semantic === 'sequence' && !isStepCollapsed(step)"
            class="motion-tree-children"
          >
            <CameraMotionTemplateNodeEditor
              v-model="step.template"
              :owner-step="step"
              :leaf-templates="leafTemplates"
              :semantic-options="semanticOptions"
              :param-fields="paramFields"
              :depth="depth + 1"
            />
          </div>
        </div>
      </div>

      <div class="motion-node-actions">
        <button
          type="button"
          :disabled="sequenceTemplate.params.steps.length >= 32"
          @click="addLeafStep"
        >添加子运镜</button>
        <button
          type="button"
          :disabled="!canNest || sequenceTemplate.params.steps.length >= 32"
          @click="addSequenceStep"
        >添加子组合</button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.motion-node {
  display: grid;
  gap: 10px;
}

.motion-node-root {
  gap: 12px;
}

.motion-selected-editor {
  display: grid;
  gap: 12px;
  padding: 12px;
  border: 1px solid rgb(79 195 247 / 35%);
  border-radius: 8px;
  background: rgb(79 195 247 / 7%);
}

.motion-selected-header,
.motion-tree-header,
.motion-tree-row,
.motion-node-actions,
.motion-tree-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.motion-selected-header > div,
.motion-tree-header > div {
  display: grid;
  gap: 3px;
}

.motion-section-label,
.motion-selected-depth,
.motion-tree-header > span {
  color: rgb(255 255 255 / 58%);
  font-size: 12px;
}

.motion-node-meta,
.motion-leaf-params,
.motion-offsets {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.motion-node label,
.motion-offset-group {
  display: grid;
  gap: 4px;
  min-width: 0;
  color: rgb(255 255 255 / 68%);
  font-size: 12px;
}

.motion-node input,
.motion-node select,
.motion-node button {
  min-width: 0;
  padding: 7px 8px;
  border: 1px solid rgb(255 255 255 / 14%);
  border-radius: 6px;
  color: inherit;
  background: rgb(14 18 24 / 92%);
}

.motion-node-actions button,
.motion-tree-actions button {
  cursor: pointer;
  transition:
    border-color 160ms ease,
    background-color 160ms ease,
    color 160ms ease,
    transform 160ms ease;
}

.motion-node-actions button:not(:disabled):hover,
.motion-tree-actions button:not(:disabled):hover {
  border-color: rgb(79 195 247 / 72%);
  color: rgb(255 255 255 / 96%);
  background: rgb(79 195 247 / 18%);
  transform: translateY(-1px);
}

.motion-node-actions button:not(:disabled):active,
.motion-tree-actions button:not(:disabled):active {
  background: rgb(79 195 247 / 26%);
  transform: translateY(0);
}

.motion-tree-actions button:last-child:not(:disabled):hover {
  border-color: rgb(255 107 107 / 70%);
  color: rgb(255 180 180);
  background: rgb(255 107 107 / 14%);
}

.motion-node-actions button:focus-visible,
.motion-tree-actions button:focus-visible {
  outline: 2px solid rgb(79 195 247 / 70%);
  outline-offset: 2px;
}

.motion-node-actions button:disabled,
.motion-tree-actions button:disabled {
  cursor: not-allowed;
  opacity: 0.42;
}

.motion-tree-list,
.motion-tree-branch,
.motion-tree-children,
.motion-leaf-editor,
.motion-offset-section,
.motion-offset-title {
  display: grid;
  gap: 8px;
}

.motion-tree-row {
  padding: 6px;
  border: 1px solid rgb(255 255 255 / 12%);
  border-radius: 7px;
  background: rgb(0 0 0 / 14%);
}

.motion-tree-row.active {
  border-color: rgb(79 195 247 / 75%);
  background: rgb(79 195 247 / 12%);
}

.motion-tree-toggle,
.motion-tree-toggle-spacer {
  flex: 0 0 30px;
  width: 30px;
}

.motion-tree-summary {
  display: grid;
  flex: 1 1 auto;
  gap: 3px;
  text-align: left;
}

.motion-tree-summary span {
  color: rgb(255 255 255 / 58%);
  font-size: 12px;
}

.motion-tree-actions {
  flex: 0 0 auto;
  justify-content: flex-end;
}

.motion-tree-children {
  margin-left: 18px;
  padding-left: 12px;
  border-left: 1px solid rgb(79 195 247 / 32%);
}

.motion-node-actions {
  justify-content: flex-start;
}

.motion-empty-hint,
.motion-selected-empty,
.motion-first-step-hint,
.motion-sequence-detail-hint,
.motion-offset-section {
  padding: 8px;
  border: 1px solid rgb(79 195 247 / 22%);
  border-radius: 6px;
  background: rgb(79 195 247 / 7%);
}

.motion-empty-hint,
.motion-selected-empty,
.motion-first-step-hint,
.motion-sequence-detail-hint {
  margin: 0;
  color: rgb(255 255 255 / 72%);
  font-size: 12px;
  line-height: 1.5;
}

.motion-offset-title span {
  color: rgb(255 255 255 / 62%);
  font-size: 12px;
  line-height: 1.5;
}

.motion-offset-group {
  padding: 8px;
  border-radius: 6px;
  background: rgb(255 255 255 / 4%);
}

@media (max-width: 720px) {
  .motion-node-meta,
  .motion-leaf-params,
  .motion-offsets {
    grid-template-columns: 1fr;
  }

  .motion-tree-row {
    align-items: stretch;
    flex-wrap: wrap;
  }

  .motion-tree-summary {
    min-width: calc(100% - 42px);
  }

  .motion-tree-actions {
    width: 100%;
    padding-left: 38px;
  }
}
</style>
